import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, User } from "lucide-react";
import Thumb from "@/components/Thumb";

const STATUTS = {
  actif: { label: "Actif", cls: "border-emerald-500/40 text-emerald-600 dark:text-emerald-400" },
  expire_bientot: { label: "Expire bientôt", cls: "border-amber-500/40 text-amber-600 dark:text-amber-400" },
  expire: { label: "Expiré", cls: "border-red-500/40 text-red-600 dark:text-red-400" },
};

export default function MembrePicker({ abonnement, onClose, onSelect }) {
  const [membres, setMembres] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!abonnement) return;
    setSearch("");
    api.get("/membres").then((r) => setMembres(r.data)).catch(() => {});
  }, [abonnement]);

  const resultats = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q ? membres.filter((m) => `${m.prenom} ${m.nom} ${m.telephone}`.toLowerCase().includes(q)) : membres;
    return list.slice(0, 8);
  }, [search, membres]);

  return (
    <Dialog open={!!abonnement} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border text-foreground max-w-md" data-testid="membre-picker-dialog">
        <DialogHeader>
          <DialogTitle>{abonnement?.nom} — pour quel membre ?</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <p className="text-xs text-muted-foreground">La date de fin du membre sera prolongée automatiquement après paiement.</p>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un membre…" data-testid="membre-picker-search" className="pl-9 bg-background border-border text-foreground" />
          </div>
          <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
            {resultats.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Aucun membre</p>}
            {resultats.map((m) => (
              <button
                key={m.id}
                type="button"
                data-testid={`membre-picker-item-${m.id}`}
                onClick={() => onSelect(m)}
                className="w-full flex items-center gap-3 bg-background border border-border rounded-lg px-3 py-2 text-left hover:border-emerald-500/50 hover:bg-emerald-500/5 transition-colors"
              >
                <Thumb path={m.image_path} alt={m.nom} icon={User} className="w-9 h-9" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground truncate">{m.prenom} {m.nom}</p>
                  <p className="text-xs text-muted-foreground">fin {new Date(`${m.date_fin}T12:00:00`).toLocaleDateString("fr-FR")}</p>
                </div>
                <Badge variant="outline" className={STATUTS[m.statut].cls}>{STATUTS[m.statut].label}</Badge>
              </button>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onSelect(null)} className="border-border text-foreground/80" data-testid="membre-picker-sans-membre">Sans membre</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
